import { memo, useCallback, useMemo, useState } from 'react';

import { merge } from '@/components';
import { Button, Flex, Heading, Text } from '@/components/core';
import { CloseIcon, SaveIcon } from '@/components/icons';
import { toAgentRunId, useDeleteAgentRun } from '@/hooks/api';
import { useInboxStore } from '@/store';
import { DivProps } from '@/utils';

export type DeleteAgentRunsProps = Omit<DivProps, 'children'> & {
  agentId: string;
  close?: () => void;
};

const IDeleteAgentRuns = ({ agentId, close, className, ...rest }: DeleteAgentRunsProps) => {
  const selectedRunIds = useInboxStore((state) => state.selectedRunIds);
  const setSelectedRunIds = useInboxStore((state) => state.setSelectedRunIds);

  const [isDeleting, setIsDeleting] = useState(false);
  const { mutateAsync: deleteAgentRun } = useDeleteAgentRun();

  const onDeleteAgentRun = useCallback(async () => {
    setIsDeleting(true);
    for (const runId of selectedRunIds) {
      await deleteAgentRun(toAgentRunId(agentId, runId));
    }
    setIsDeleting(false);
    setSelectedRunIds([]);
    close?.();
  }, [agentId, close, deleteAgentRun, selectedRunIds, setSelectedRunIds]);

  const { heading, descriptionPart } = useMemo(() => {
    if (selectedRunIds.size === 1) {
      return { heading: 'Delete run?', descriptionPart: 'this run' };
    }
    return { heading: `Delete runs?`, descriptionPart: `${selectedRunIds.size} runs` };
  }, [selectedRunIds.size]);

  return (
    <Flex col className={merge('gap-md', className)} {...rest}>
      <Flex className="items-start">
        <Flex col className="gap-xs">
          <Heading>{heading}</Heading>
          <Text>
            Are you sure you want to delete {descriptionPart}?<br />
            This action cannot be undone.
          </Text>
        </Flex>
        <div className="grow" />
        <Button onPress={close} variant="plain">
          <CloseIcon />
        </Button>
      </Flex>
      <Flex className="gap-sm justify-end">
        <Button onPress={close} variant="secondary" isDisabled={isDeleting}>
          <Text>Cancel</Text>
        </Button>
        <Button onPress={onDeleteAgentRun} variant="danger" isDisabled={isDeleting}>
          {isDeleting ? <SaveIcon /> : null}
          <Text>Delete</Text>
        </Button>
      </Flex>
    </Flex>
  );
};

export const DeleteAgentRuns = memo(IDeleteAgentRuns) as typeof IDeleteAgentRuns;
